import { type GetServerSidePropsContext, type GetStaticPropsContext } from "next"
import {
    getKrakenErrorDetails,
    isErrorWithMessage,
    isKrakenErrorResponse,
} from "@krakentech/blueprint-api"
import { type ErrorIntlConfig } from "../types/errorIntlConfig"
import { type Namespace } from "../types/namespace"
import { createFormatServerSideErrorCode } from "./createFormatServerSideErrorCode"

type CreateFormatServerSideRequestErrorConfig<
    TMessages,
    TNamespace extends Namespace<TMessages>,
> = ErrorIntlConfig<TMessages, TNamespace>

type FormatServerSideRequestErrorProps = {
    context: GetServerSidePropsContext | GetStaticPropsContext
    error: unknown
}

export function createFormatServerSideRequestError<
    TMessages,
    TNamespace extends Namespace<TMessages>,
>({
    namespace,
    formatErrorCode,
    getMessages,
}: CreateFormatServerSideRequestErrorConfig<TMessages, TNamespace>) {
    return async function formatServerSideRequestError({
        context,
        error,
    }: FormatServerSideRequestErrorProps) {
        try {
            if (typeof error === "string") {
                return error
            }

            const formatServerSideErrorCode = createFormatServerSideErrorCode({
                namespace,
                formatErrorCode,
                getMessages,
            })

            if (isKrakenErrorResponse(error)) {
                const [firstError] = error.response.errors

                if (firstError) {
                    const { errorDescription, errorCode } = getKrakenErrorDetails(firstError)

                    if (errorCode) {
                        return formatServerSideErrorCode({ context, errorCode })
                    }

                    if (errorDescription) {
                        return errorDescription
                    }
                }
            }

            if (isErrorWithMessage(error)) {
                return error.message
            }

            return formatServerSideErrorCode({ context, errorCode: "UNKNOWN_ERROR" })
        } catch (error) {
            throw new Error(`An error occured in "formatServerSideErrorCode".`, { cause: error })
        }
    }
}
